import { useStaticCallback } from '@sparemin/blockhead';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'underscore';
import { createDeferred, Deferred } from 'utils/promise';
import { useClipSelect } from '../../ClipSelectContext';
import { isProcessingSelector } from '../selectors';

export type UseReprocessFlowProps = {
  /**
   * how long to wait after receiving reprocess requests before fulfilling them
   */
  debounceMillis?: number;
  suggestionId: number;
};

/**
 * Handles reprocessing the preview, both immediately and after a debounce for an
 * "autosave" impelmentation
 */
export default function useReprocessFlow({
  debounceMillis = 2000,
  suggestionId,
}: UseReprocessFlowProps) {
  const { onReprocessClip, suggestion } = useClipSelect({
    clipId: suggestionId,
  });

  const isPreviewDirtyRef = useRef(suggestion.isPreviewOutdated);
  const activeReprocessRef = useRef<Deferred | null>(null);
  const pendingReprocessRef = useRef<Deferred | null>(null);
  const isProcessing = isProcessingSelector(suggestion);

  const staticReprocess = useStaticCallback(async () => {
    // if already processing, don't start a new job
    if (isProcessingSelector(suggestion)) {
      // if there's already a pending reprocess job, return the promsie for it
      if (pendingReprocessRef.current) {
        return pendingReprocessRef.current.promise;
      }

      // if there's no pending reprocess job, create one and return the promise
      pendingReprocessRef.current = createDeferred();
      return pendingReprocessRef.current.promise;
    }

    // not currently processing, so kickoff the job and return the promise
    activeReprocessRef.current = createDeferred();
    isPreviewDirtyRef.current = false;

    // start the work. will detect completion in the effect hook
    onReprocessClip({ context: 'preview' });

    return activeReprocessRef.current.promise;
  });

  const debouncedReprocess = useMemo(
    () => debounce(staticReprocess, debounceMillis),
    [debounceMillis, staticReprocess],
  );

  const isPreviewDirty = useCallback(() => isPreviewDirtyRef.current, []);

  const isPendingReprocess = useCallback(
    () => !!pendingReprocessRef.current,
    [],
  );

  const reprocess = useCallback(() => {
    isPreviewDirtyRef.current = true;
    return debouncedReprocess();
  }, [debouncedReprocess]);

  const cancelReprocess = useCallback(() => {
    debouncedReprocess.cancel();

    if (pendingReprocessRef.current) {
      pendingReprocessRef.current.reject(new Error('unmounted'));
      pendingReprocessRef.current = null;
    }

    if (activeReprocessRef.current) {
      activeReprocessRef.current.reject(new Error('unmounted'));
      activeReprocessRef.current = null;
    }
  }, [debouncedReprocess]);

  const reprocessNow = useCallback(() => {
    cancelReprocess();
    return staticReprocess();
  }, [cancelReprocess, staticReprocess]);

  useEffect(
    () => () => {
      debouncedReprocess.cancel();

      if (pendingReprocessRef.current) {
        pendingReprocessRef.current.reject(new Error('unmounted'));
        pendingReprocessRef.current = null;
      }
      if (activeReprocessRef.current) {
        activeReprocessRef.current.reject(new Error('unmounted'));
        activeReprocessRef.current = null;
      }
    },
    [cancelReprocess, debouncedReprocess],
  );

  // this effect should only run when isProcessing changes.  pendingReprocess is
  // a ref and won't cause the effect to rerun, while debouncedReprocess is a
  // static function
  useEffect(() => {
    if (
      isProcessing &&
      !activeReprocessRef.current &&
      pendingReprocessRef.current
    ) {
      activeReprocessRef.current = pendingReprocessRef.current;
      pendingReprocessRef.current = null;
    }

    if (!isProcessing && activeReprocessRef.current) {
      activeReprocessRef.current.resolve();
      activeReprocessRef.current = null;

      if (pendingReprocessRef.current) {
        activeReprocessRef.current = pendingReprocessRef.current;
        pendingReprocessRef.current = null;

        onReprocessClip({ context: 'preview' });
      }
    }
  }, [debouncedReprocess, isProcessing, onReprocessClip]);

  return {
    isPreviewDirty,
    isPendingReprocess,
    // drops any pending reprocesses
    cancelReprocess,
    // mark dirty and debounce
    reprocess,
    // flush pending reprocess immediately
    reprocessNow,
    // in rare cases for scheduling externally
    debouncedReprocess,
  };
}
